import { useMemo, useLayoutEffect, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { resetStepsDataSet, stepMainIdIndicatorSet, stepStructureSet } from 'store/steps';
import { resetUserDataSet } from 'store/userData';
import { stepTypeEnum, journeyTypes, defaultStepPropertyTypeListEnum, userDataBySteps, questionTypeEnum } from 'assets/constants';
import { queryClient } from 'index';
import ReactGA from "react-ga4";
import { updateObjectAfterFirstFalse } from 'components/NavigationHeader/utils';
import { stepHasMissingData } from 'utils';

const { stepDetails, stepSetAppointment, stepSetAppointmentReschedule, stepSetAppointmentQuickReschedule, stepServiceType, stepQuestions, stepQuestionReschedule, stepQuestionQuickReschedule, stepLocation, stepService, stepDateAndTime, stepDateAndTimeReschedule, stepDateAndTimeQuickReschedule, stepAppointmentDetailsReschedule } = stepTypeEnum;

export function useActiveSteps(data, stepCustomerDetailsDisabled, hasError) {
  const stepStructureUpdate = useStepStructureUpdate();

  const steps = useMemo(() => {
    if (!data) return;

    const filteredData = (data.journeyTypeId === journeyTypes.quickReschedule && !hasError) ? filterErrorStep(data) : data
    return filteredData.steps?.filter(({ type, properties }) =>
      type !== stepDetails || (!stepCustomerDetailsDisabled && properties.properties.length > 1)
    );
  }, [data, stepCustomerDetailsDisabled, hasError]);

  useLayoutEffect(() => { steps?.length && stepStructureUpdate(steps) }, [steps]);

  return steps;
}

export const filterErrorStep = (data) => {
  const filteredData = {
    ...data,
    steps: data.steps.filter(step => step.type !== 24)
  };
  return filteredData;
};

export function useStepTypeInputStatus(steps, userData, activeStep, stepStructure) {

  return useMemo(() => {
    if (!steps) return;
    return stepStructure.reduce((properties, property, index) => {
      //here will go all steps that are not mandatory
      if (property === stepTypeEnum[stepDetails]) {
        const propertyNumber = stepTypeEnum[property]
        properties[index] = { [propertyNumber]: true};
        return properties;
      }

      if ( property === stepTypeEnum[stepSetAppointment] ) {
        const propertyNumber = stepTypeEnum[property]
        properties[index] = {[propertyNumber] :true};
        return properties;
      }
      if ( property === stepTypeEnum[stepSetAppointmentReschedule] ) {
        const propertyNumber = stepTypeEnum[property]
        properties[index] = {[propertyNumber] :true};
        return properties;
      }

      if (property === stepTypeEnum[stepSetAppointmentQuickReschedule]) {
        const propertyNumber = stepTypeEnum[property]
        properties[index] = {[propertyNumber]: true};
        return properties;
      }

      if (property === stepTypeEnum[stepAppointmentDetailsReschedule]) {
        const propertyNumber = stepTypeEnum[property]
        properties[index] = {[propertyNumber]: true};
        return properties;
      }
      //in this step we have more than one mandatory option that not allways turned on
      if (property === stepTypeEnum[stepServiceType]) {
        // NOTE: it is possible to configurate the service and appointment types at the same time
        // In this case, the next button will be enable after selecting the service type even if the appointmen type is not selected
        const hasServiceTypeSelected = !!userData?.clientStateData?.serviceTypeId || !!userData['serviceType']?.id
        const hasAppointmentTypeSelected = !!userData?.clientStateData?.appointmentTypeId || !!userData['appointmentType']?.id
        const hasSelectedValue = hasServiceTypeSelected || hasAppointmentTypeSelected;

        const propertyNumber = stepTypeEnum[property]

        const questionsInputStatus = questionsInputStatusByStep(steps, index , userData)
        properties[index] = {[propertyNumber] : questionsInputStatus && hasSelectedValue};
        return properties;
      }
      if (property === stepTypeEnum[stepLocation]) {
        const propertyNumber = stepTypeEnum[property]
        const userDataValue = !!userData?.clientStateData?.unitId || !!userData['location']?.id
        const questionsInputStatus = questionsInputStatusByStep(steps, index , userData)
        properties[index] = {[propertyNumber] : userDataValue && questionsInputStatus};
        return properties
      }
      if (property === stepTypeEnum[stepService]) {
        const propertyNumber = stepTypeEnum[property]
        const userDataValue = !!userData?.clientStateData?.serviceId || !!userData['service']?.id
        const questionsInputStatus = questionsInputStatusByStep(steps, index , userData)
        properties[index] = {[propertyNumber] : userDataValue && questionsInputStatus};
        return properties
      }
      if (property === stepTypeEnum[stepDateAndTime]
        || property === stepTypeEnum[stepDateAndTimeReschedule]
        || property === stepTypeEnum[stepDateAndTimeQuickReschedule]) {
          const propertyNumber = stepTypeEnum[property]
        const userDataValue = !!userData?.clientStateData?.selectedSlot || !!userData['dateAndTime']
        properties[index] = { [propertyNumber] : userDataValue}
        return properties
      }
      if (property === stepTypeEnum[stepQuestions]
        || property === stepTypeEnum[stepQuestionReschedule]
        || property === stepTypeEnum[stepQuestionQuickReschedule]) {
          const propertyNumber = stepTypeEnum[property]
        const questionsInputStatus = questionsInputStatusByStep(steps, index , userData)
        properties[index] = {[propertyNumber] : questionsInputStatus};
        return properties
      }
      const currentName = stepTypeEnum[property]
      properties[index] = {[currentName]: !!userData[userDataBySteps[property]]};
      return properties;
    }, {})
  }, [userData, steps, activeStep, stepStructure]);
}
function isSetAppointmentStep(stepType) {
  return stepType == stepTypeEnum.stepSetAppointment
    || stepType == stepTypeEnum.stepSetAppointmentReschedule
    || stepType == stepTypeEnum.stepSetAppointmentQuickReschedule;
}
function isAppointmentDetailsStep(stepType) {
  return stepType == stepTypeEnum.stepAppointmentDetails
    || stepType == stepTypeEnum.stepAppointmentDetailsReschedule
    || stepType == stepTypeEnum.stepAppointmentDetailsQuickReschedule;
}

function useStepStructureUpdate() {

  const dispatch = useDispatch();
  const backToFirstStep = useBackToFirstStep();
  const stepMainIdIndicator = useSelector(({ current }) => current.steps.stepMainIdIndicator)

  return (steps) => {
    if (!steps) return;

    const currentMainIdIndicator = steps.find((step) => isSetAppointmentStep(step.type))?.id

    if (!!stepMainIdIndicator && stepMainIdIndicator !== currentMainIdIndicator) {
      console.log('resetting because structure changed');
      backToFirstStep(true);
      return;
    }

    dispatch(stepStructureSet(steps.map((step) => stepTypeEnum[step.type])));
    dispatch(stepMainIdIndicatorSet(currentMainIdIndicator));

  }
}

function questionsInputStatusByStep(steps, index, userData) {
  const { properties: stepProperties } = steps[index]?.properties || {};

  const questionsInputStatus = stepProperties.every((stepProperty, i) => {
    if (stepProperty.type === defaultStepPropertyTypeListEnum.question || stepProperty.type === questionTypeEnum.multiSelection || stepProperty.type === questionTypeEnum.freeText) {
      const { questionKey } = stepProperty?.value?.question || {};
      const { isMandatory, hasRegex, regex } = stepProperty?.value?.questionConfig || {};
      const questions = userData[`questions_${index}`] || {}
      const questionsWithNotValidAnswers = userData[`questions_with_not_valid_answers_${index}`] || {}
      const dontShowThisQuestion = userData['dont_show_question'] ? !!userData['dont_show_question']?.hasOwnProperty(questionKey) : false;
      const hasAnswer = !!questions && !!userData[`questions_${index}`]?.hasOwnProperty(questionKey) 
      const hasNotValidAnswer = !!questionsWithNotValidAnswers && !!userData[`questions_with_not_valid_answers_${index}`]?.hasOwnProperty(questionKey)
      const isEmptyAnswer = hasAnswer && stepProperty.type === questionTypeEnum.freeText && (questions[questionKey] === "");
      const isEmptyAnswerNotFromFreeText = hasAnswer && stepProperty.type !== questionTypeEnum.freeText && (questions[questionKey] === "") ;
      const validInput = (hasRegex && !!regex?.length) ? new RegExp(regex).test(questions[questionKey]) : true;
      
      const inputFalseStatus = isMandatory && !dontShowThisQuestion &&  ( !hasAnswer || isEmptyAnswer || isEmptyAnswerNotFromFreeText || !validInput || hasNotValidAnswer)
      if (inputFalseStatus)
        return false;
    }
    return true;
  });
  return questionsInputStatus
}

export function useAnalyticsEventPageView(page) {
  useEffect(() => { ReactGA.send({ hitType: "pageview", page: `/${page}`, title: `${page}` }) }, []);
}

export function useAnalyticsEventTracker(category = "Online Booking") {
  const eventTracker = (action = "action", label = "label") => {
    ReactGA.event({ category, action, label });
  }
  return eventTracker;
}

export function useFindProperty(properties, typeToFind) {
  return useMemo(() => properties?.find(({ type }) => type === typeToFind)?.value, [properties, typeToFind])
}

export function currentStepBlocker(
  steps,
  activeStep,
  stepTypeInputStatus,
  customerIdentificationStepPosition,
  hasToken,
  popupState,
  blockNextButton
) {

  const stepType = steps[activeStep]?.type;
  const mapedStepTypeInputStatus = updateObjectAfterFirstFalse(stepTypeInputStatus)
  const hasMissingData =  stepHasMissingData(mapedStepTypeInputStatus, activeStep);
  const hasSetAppointmentStep = isSetAppointmentStep(stepType)
  const hasAppointmentDetailsStep = isAppointmentDetailsStep(stepType)

  return (
    (hasMissingData && !hasSetAppointmentStep)
    || hasAppointmentDetailsStep
    || ((customerIdentificationStepPosition !== -1 && customerIdentificationStepPosition <= activeStep) && !hasToken)
    || popupState.open
    || blockNextButton
  )
}

export function useScrollToTop() {
  const containerRef = useRef();
  const activeStep = useSelector(({ current }) => current.steps.activeStep)

  useLayoutEffect(() => {
    containerRef?.current?.scroll({ top: 0 }, true);
  }, [activeStep])

  return containerRef;
}

export function useBackToFirstStep() {
  const dispatch = useDispatch();

  return (reset) => {
    if (reset) {
      queryClient.removeQueries({ queryKey: ['active-query'] });
      sessionStorage.removeItem("end_user_token");
    }
    else {
      queryClient.removeQueries({ queryKey: ['active-query', 'customer-info'] });
      queryClient.invalidateQueries({ queryKey: ['active-query'] });
    }

    dispatch(resetUserDataSet());
    dispatch(resetStepsDataSet());
  }

}